import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppBackButton } from '../components/AppBackButton';
import { ArrowLeft, ChevronDown, Store } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export const STORE_LANGUAGES = [
  { id: 'ptbr', name: 'Português', flag: '🇧🇷' },
  { id: 'en', name: 'Inglês', flag: '🇺🇸' },
  { id: 'es', name: 'Espanhol', flag: '🇪🇸' },
  { id: 'fr', name: 'Francês', flag: '🇫🇷' },
  { id: 'de', name: 'Alemão', flag: '🇩🇪' },
  { id: 'it', name: 'Italiano', flag: '🇮🇹' },
  { id: 'hu', name: 'Húngaro', flag: '🇭🇺' },
  { id: 'ru', name: 'Russo', flag: '🇷🇺' }
];

export const STORE_TRANSLATIONS: Record<string, Record<string, string>> = {
  pt: {
    backToFlashcards: "Voltar para Flashcards",
    storeTitle: "LOJA DE FLASHCARDS",
    storeSubtitle: "IMPORTE BARALHOS PRONTOS PARA ACELERAR SEU APRENDIZADO",
    myLanguage: "MEU IDIOMA",
    imLearning: "ESTOU APRENDENDO",
    search: "Procurar",
    workingOnIt: "Estamos trabalhando nisso...",
    storeButton: "Loja",
    importDirectly: "Importe baralhos diretamente da ",
    librituiStore: "loja Libritui",
    lang_ptbr: "Português",
    lang_en: "Inglês",
    lang_es: "Espanhol",
    lang_fr: "Francês",
    lang_de: "Alemão",
    lang_it: "Italiano",
    lang_hu: "Húngaro",
    lang_ja: "Japonês",
    lang_zh: "Chinês",
    lang_ru: "Russo"
  },
  en: {
    backToFlashcards: "Back to Flashcards",
    storeTitle: "FLASHCARD STORE",
    storeSubtitle: "IMPORT READY-MADE DECKS TO ACCELERATE YOUR LEARNING",
    myLanguage: "MY LANGUAGE",
    imLearning: "I AM LEARNING",
    search: "Search",
    workingOnIt: "We are working on this...",
    storeButton: "Store",
    importDirectly: "Import decks directly from the ",
    librituiStore: "Libritui store",
    lang_ptbr: "Portuguese",
    lang_en: "English",
    lang_es: "Spanish",
    lang_fr: "French",
    lang_de: "German",
    lang_it: "Italian",
    lang_hu: "Hungarian",
    lang_ja: "Japanese",
    lang_zh: "Chinese",
    lang_ru: "Russian"
  },
  es: {
    backToFlashcards: "Volver a Flashcards",
    storeTitle: "TIENDA DE FLASHCARDS",
    storeSubtitle: "IMPORTA MAZOS LISTOS PARA ACELERAR TU APRENDIZAJE",
    myLanguage: "MI IDIOMA",
    imLearning: "ESTOY APRENDIENDO",
    search: "Buscar",
    workingOnIt: "Estamos trabajando en ello...",
    storeButton: "Tienda",
    importDirectly: "Importa mazos directamente de la ",
    librituiStore: "tienda Libritui",
    lang_ptbr: "Portugués",
    lang_en: "Inglés",
    lang_es: "Español",
    lang_fr: "Francés",
    lang_de: "Alemán",
    lang_it: "Italiano",
    lang_hu: "Húngaro",
    lang_ja: "Japonés",
    lang_zh: "Chino",
    lang_ru: "Ruso"
  },
  fr: {
    backToFlashcards: "Retour aux Flashcards",
    storeTitle: "BOUTIQUE DE FLASHCARDS",
    storeSubtitle: "IMPORTEZ DES PAQUETS PRÊTS POUR ACCÉLÉRER VOTRE APPRENTISSAGE",
    myLanguage: "MON LANGUE",
    imLearning: "J'APPRENDS",
    search: "Rechercher",
    workingOnIt: "Nous y travaillons...",
    storeButton: "Boutique",
    importDirectly: "Importez des paquets directement depuis la ",
    librituiStore: "boutique Libritui",
    lang_ptbr: "Portugais",
    lang_en: "Anglais",
    lang_es: "Espagnol",
    lang_fr: "Français",
    lang_de: "Allemand",
    lang_it: "Italien",
    lang_hu: "Hongrois",
    lang_ja: "Japonais",
    lang_zh: "Chinois",
    lang_ru: "Russe"
  },
  de: {
    backToFlashcards: "Zurück zu Karteikarten",
    storeTitle: "KARTEIKARTEN-SHOP",
    storeSubtitle: "IMPORTIEREN SIE FERTIGE STAPEL, UM IHR LERNEN ZU BESCHLEUNIGEN",
    myLanguage: "MEINE SPRACHE",
    imLearning: "ICH LERNE",
    search: "Suchen",
    workingOnIt: "Wir arbeiten daran...",
    storeButton: "Shop",
    importDirectly: "Importieren Sie Stapel direkt aus dem ",
    librituiStore: "Libritui-Shop",
    lang_ptbr: "Portugiesisch",
    lang_en: "Englisch",
    lang_es: "Spanisch",
    lang_fr: "Französisch",
    lang_de: "Deutsch",
    lang_it: "Italienisch",
    lang_hu: "Ungarisch",
    lang_ja: "Japanisch",
    lang_zh: "Chinesisch",
    lang_ru: "Russisch"
  },
  it: {
    backToFlashcards: "Torna a Flashcard",
    storeTitle: "NEGOZIO DI FLASHCARD",
    storeSubtitle: "IMPORTA MAZZI PRONTI PER ACCELERARE IL TUO APPRENDIMENTO",
    myLanguage: "LA MIA LINGUA",
    imLearning: "STO IMPARANDO",
    search: "Cerca",
    workingOnIt: "Ci stiamo lavorando...",
    storeButton: "Negozio",
    importDirectly: "Importa i mazzi direttamente dal ",
    librituiStore: "negozio Libritui",
    lang_ptbr: "Portoghese",
    lang_en: "Inglese",
    lang_es: "Spagnolo",
    lang_fr: "Francese",
    lang_de: "Tedesco",
    lang_it: "Italiano",
    lang_hu: "Ungherese",
    lang_ja: "Giapponese",
    lang_zh: "Cinese",
    lang_ru: "Russo"
  },
  hu: {
    backToFlashcards: "Vissza a Tanulókártyákhoz",
    storeTitle: "TANULÓKÁRTYA BOLT",
    storeSubtitle: "IMPORTÁLJON KÉSZ KÁRTYAPAKLIKAT A TANULÁS FELGYORSÍTÁSÁHOZ",
    myLanguage: "SAJÁT NYELVEM",
    imLearning: "TANULOK",
    search: "Keresés",
    workingOnIt: "Már dolgozunk rajta...",
    storeButton: "Bolt",
    importDirectly: "Importáljon kártyákat közvetlenül a ",
    librituiStore: "Libritui boltból",
    lang_ptbr: "Portugál",
    lang_en: "Angol",
    lang_es: "Spanyol",
    lang_fr: "Francia",
    lang_de: "Német",
    lang_it: "Olasz",
    lang_hu: "Magyar",
    lang_ja: "Japán",
    lang_zh: "Kínai",
    lang_ru: "Orosz"
  },
  ja: {
    backToFlashcards: "フラッシュカードに戻る",
    storeTitle: "フラッシュカードストア",
    storeSubtitle: "学習をスピードアップするために、既製のデッキをインポートします",
    myLanguage: "私の言語",
    imLearning: "学習中の言語",
    search: "検索",
    workingOnIt: "現在、開発中です...",
    storeButton: "ストア",
    importDirectly: "Librituiストアから直接デッキをインポートする： ",
    librituiStore: "Librituiストア",
    lang_ptbr: "ポルトガル語",
    lang_en: "英語",
    lang_es: "スペイン語",
    lang_fr: "フランス語",
    lang_de: "ドイツ語",
    lang_it: "イタリア語",
    lang_hu: "ハンガリー語",
    lang_ja: "日本語",
    lang_zh: "中国語",
    lang_ru: "ロシア語"
  },
  zh: {
    backToFlashcards: "返回闪卡",
    storeTitle: "闪卡商店",
    storeSubtitle: "导入现成的卡片集以加速您的学习",
    myLanguage: "我的母语",
    imLearning: "我正在学习",
    search: "搜索",
    workingOnIt: "我们正在努力开发中...",
    storeButton: "商店",
    importDirectly: "直接从Libritui商店导入卡片集： ",
    librituiStore: "Libritui商店",
    lang_ptbr: "葡萄牙语",
    lang_en: "英语",
    lang_es: "西班牙语",
    lang_fr: "法语",
    lang_de: "德语",
    lang_it: "意大利语",
    lang_hu: "匈牙利语",
    lang_ja: "日语",
    lang_zh: "中文",
    lang_ru: "俄语"
  },
  ru: {
    backToFlashcards: "Назад к карточкам",
    storeTitle: "МАГАЗИН КАРТОЧЕК",
    storeSubtitle: "ИМПОРТИРУЙТЕ ГОТОВЫЕ КОЛОДЫ ДЛЯ УСКОРЕНИЯ ОБУЧЕНИЯ",
    myLanguage: "МОЙ ЯЗЫК",
    imLearning: "Я УЧУ",
    search: "Найти",
    workingOnIt: "Мы работаем над этим...",
    storeButton: "Магазин",
    importDirectly: "Импортируйте колоды прямо из ",
    librituiStore: "магазина Libritui",
    lang_ptbr: "Португальский",
    lang_en: "Английский",
    lang_es: "Испанский",
    lang_fr: "Французский",
    lang_de: "Немецкий",
    lang_it: "Итальянский",
    lang_hu: "Венгерский",
    lang_ja: "Японский",
    lang_zh: "Китайский",
    lang_ru: "Русский"
  }
};

const FlashcardStorePage: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const currentLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
  const getStoreTranslation = (key: string) => {
    const translations = STORE_TRANSLATIONS[currentLang] || STORE_TRANSLATIONS.pt;
    return translations[key] || STORE_TRANSLATIONS.pt[key];
  };

  // Dropdown States
  const [myLang, setMyLang] = useState(() => localStorage.getItem('fc_store_my_lang') || 'ptbr');
  const [learningLang, setLearningLang] = useState(() => localStorage.getItem('fc_store_learning_lang') || 'en');
  const [isMyLangOpen, setIsMyLangOpen] = useState(false);
  const [isLearningLangOpen, setIsLearningLangOpen] = useState(false);

  // Refs for clicking outside
  const myLangRef = useRef<HTMLDivElement>(null);
  const learningLangRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (myLangRef.current && !myLangRef.current.contains(event.target as Node)) {
        setIsMyLangOpen(false);
      }
      if (learningLangRef.current && !learningLangRef.current.contains(event.target as Node)) {
        setIsLearningLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    localStorage.setItem('fc_store_my_lang', myLang);
    localStorage.setItem('fc_store_learning_lang', learningLang);
    navigate(`/flashcards/store/select/${myLang}/${learningLang}`);
  };

  const getLangName = (id: string) => {
    return getStoreTranslation(`lang_${id}`) || STORE_LANGUAGES.find(l => l.id === id)?.name || id;
  };

  const selectedMyLangObj = STORE_LANGUAGES.find(l => l.id === myLang) || STORE_LANGUAGES[0];
  const selectedLearningLangObj = STORE_LANGUAGES.find(l => l.id === learningLang) || STORE_LANGUAGES[1];

  return (
    <div className="min-h-full flex flex-col bg-slate-50 dark:bg-slate-950/40 pb-24">
      <style>{`
        .voice-dropdown-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .voice-dropdown-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .voice-dropdown-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.3);
          border-radius: 10px;
        }
        .voice-dropdown-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.5);
        }
      `}</style>

      {/* Header */}
      <div className="sticky top-0 p-6 md:p-8 flex items-center justify-between z-50 bg-slate-50/90 dark:bg-slate-950/90 backdrop-blur-md">
        <AppBackButton fallbackTo="/flashcards" />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6 space-y-12 relative">
        <div className="text-center space-y-3">
          <div className="flex justify-center mb-2">
            <Store className="text-primary animate-pulse" size={48} />
          </div>
          <h1 className="text-4xl md:text-5xl font-medium text-ink tracking-[-0.03em]">
            {getStoreTranslation('storeTitle')}
          </h1>
          <p className="text-slate-500 font-normal uppercase font-mono tracking-[0.14em] text-[10px] max-w-md mx-auto leading-relaxed">
            {getStoreTranslation('storeSubtitle')}
          </p>
        </div>

        <div className="w-full max-w-md space-y-6 bg-surface dark:bg-surface border border-black/[0.06] dark:border-white/[0.06] p-8 rounded-[28px]">
          {/* My Language Select */}
          <div className="space-y-2">
            <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400 ml-4">
              {getStoreTranslation('myLanguage')}
            </label>
            <div className="relative" ref={myLangRef}>
              <button
                onClick={() => { setIsMyLangOpen(!isMyLangOpen); setIsLearningLangOpen(false); }}
                className="w-full bg-slate-50 dark:bg-slate-900 border border-black/[0.06] dark:border-white/[0.06] p-5 rounded-[24px] flex items-center justify-between hover:border-primary transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">{selectedMyLangObj.flag}</span>
                  <span className="font-bold dark:text-ink">{getLangName(myLang)}</span>
                </div>
                <ChevronDown size={20} className={`text-slate-400 transition-transform ${isMyLangOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMyLangOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-surface dark:bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[24px] rounded-b-[2.5rem] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] z-50 overflow-hidden">
                  <div className="max-h-64 overflow-y-auto voice-dropdown-scrollbar pb-2">
                    {STORE_LANGUAGES.map(lang => (
                      <div
                        key={lang.id}
                        onClick={() => { setMyLang(lang.id); setIsMyLangOpen(false); }}
                        className="p-4 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                      >
                        <span>{lang.flag}</span>
                        <span className="font-bold text-sm dark:text-ink">{getLangName(lang.id)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Learning Language Select */}
          <div className="space-y-2">
            <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400 ml-4">
              {getStoreTranslation('imLearning')}
            </label>
            <div className="relative" ref={learningLangRef}>
              <button
                onClick={() => { setIsLearningLangOpen(!isLearningLangOpen); setIsMyLangOpen(false); }}
                className="w-full bg-slate-50 dark:bg-slate-900 border border-black/[0.06] dark:border-white/[0.06] p-5 rounded-[24px] flex items-center justify-between hover:border-primary transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">{selectedLearningLangObj.flag}</span>
                  <span className="font-bold dark:text-ink">{getLangName(learningLang)}</span>
                </div>
                <ChevronDown size={20} className={`text-slate-400 transition-transform ${isLearningLangOpen ? 'rotate-180' : ''}`} />
              </button>
              {isLearningLangOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-surface dark:bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[24px] rounded-b-[2.5rem] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] z-50 overflow-hidden">
                  <div className="max-h-64 overflow-y-auto voice-dropdown-scrollbar pb-2">
                    {STORE_LANGUAGES.map(lang => (
                      <div
                        key={lang.id}
                        onClick={() => { setLearningLang(lang.id); setIsLearningLangOpen(false); }}
                        className="p-4 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                      >
                        <span>{lang.flag}</span>
                        <span className="font-bold text-sm dark:text-ink">{getLangName(lang.id)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSearch}
            className="w-full py-6 bg-primary hover:opacity-90 text-slate-900 rounded-[24px] font-medium text-sm hover:scale-[1.02] active:scale-95 transition-all mt-4"
          >
            {getStoreTranslation('search')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlashcardStorePage;
